import { createContext, useContext } from 'react'

import type { Cliente, Grupo, Usuario } from '@/api/contracts/administracao'
import type { PoliticaDeSenha } from '@/auth/politicaDeSenha'

/**
 * The session, as every screen sees it.
 *
 * ⚠️ **Cognito carries identity, not authorization** (PROJECT-OVERVIEW §5.1). `grupos`
 * and `clientes` are APPLICATION DATA served by the backend's `Administração`
 * database — they are never read from the token's `cognito:groups` claim, however
 * convenient that would be.
 *
 * O adaptador Cognito entrega as duas listas VAZIAS; quem as preenche é `GET /eu`,
 * injetado no provider como `carregarPerfil`. Os ids continuam morando aqui, e não
 * no perfil, porque todo consumidor já os lê daqui — trocar a origem do dado não
 * precisou mudar a forma dele.
 */
export interface Sessao {
  sub: string
  nome: string
  email: string
  grupos: string[]
  clientes: string[]
}

/** Why the session ended — it decides what `/login` says when the user lands there. */
export type MotivoSaida = 'usuario' | 'expirada'

/**
 * A sign-in that needs one more step before it becomes a session.
 *
 * Only one exists today: `novaSenha`, the `FORCE_CHANGE_PASSWORD` state every
 * invited user starts in (BR-08). MFA would be a second member — it is not
 * configured, so it is not modelled.
 */
export type DesafioDeEntrada = 'novaSenha'

/**
 * O que `GET /eu` acrescenta à identidade: os grupos COM suas permissões e os nomes
 * dos clientes que o usuário alcança.
 *
 * Separado de `Sessao` de propósito. `Sessao` é o que a autenticação produz e existe
 * assim que o usuário entra; o perfil chega depois, por uma segunda requisição que
 * pode falhar sozinha. Fundir os dois faria uma falha do backend parecer um logout.
 */
export interface PerfilDaSessao {
  grupos: Grupo[]
  clientes: Cliente[]
}

/**
 * A resposta de `GET /eu`, como o provider a recebe.
 *
 * `usuario` traz os IDS (`grupos`, `clientes`) que vão para a `Sessao`; os arrays de
 * topo trazem os objetos completos, que viram o `PerfilDaSessao`. Os dois vêm juntos
 * numa requisição só porque separá-los custaria um round-trip para nada.
 */
export interface FalhaDePerfil {
  mensagem: string
  podeTentarNovamente: boolean
}

export interface PerfilCarregado {
  usuario: Usuario
  grupos: Grupo[]
  clientes: Cliente[]
}

export interface ContextoSessao {
  /** `null` when nobody is signed in. */
  sessao: Sessao | null
  /**
   * O resultado de `GET /eu`. `null` quando ainda não chegou, quando falhou, ou no
   * modo mock — onde o adaptador já entrega grupos e clientes por fixture e o
   * endpoint não é consultado.
   */
  perfil: PerfilDaSessao | null
  /**
   * Por que o perfil não pôde ser carregado. Autenticado sem perfil é um estado real
   * e distinto de "deslogado": a tela precisa explicá-lo em vez de mostrar uma
   * aplicação vazia.
   *
   * Carrega `podeTentarNovamente` porque a tela decide com base nisso se oferece o
   * botão de repetir — e as causas se dividem exatamente ao meio: gateway fora do ar
   * pede repetição, falta de cadastro e configuração ausente não.
   */
  erroDePerfil: FalhaDePerfil | null
  /** Refaz `GET /eu` depois de uma falha, sem obrigar o usuário a entrar de novo. */
  recarregarPerfil: () => Promise<void>
  /** True while the boot restore is in flight — render nothing decisive yet. */
  carregando: boolean
  /** Set when the previous session ended by expiry; cleared once `/login` shows it. */
  avisoDeSaida: MotivoSaida | null
  /**
   * 🔒 True entre um logout DELIBERADO e a próxima entrada.
   *
   * Separado de `avisoDeSaida` de propósito: aquele é "o que mostrar na tela de
   * login", e sair pelo botão não mostra nada. Este é "de onde viemos", e quem
   * precisa dele é `RotaProtegida`: depois de um logout, o caminho onde o
   * usuário ANTERIOR estava não é destino para o PRÓXIMO — ele carrega o
   * `idCliente` de outra pessoa.
   *
   * Uma sessão EXPIRADA é o caso oposto: é o mesmo usuário voltando, e guardar
   * o destino é a coisa certa. Daí os dois estados serem distintos.
   */
  saidaDeliberada: boolean
  /** Non-null while `/login` must show a step instead of the credentials form. */
  desafio: DesafioDeEntrada | null
  entrar: (email: string, senha: string) => Promise<void>
  /** Answers `desafio === 'novaSenha'` and completes the sign-in. */
  definirNovaSenha: (novaSenha: string) => Promise<void>
  /** Abandons the challenge and returns to the credentials form. */
  cancelarDesafio: () => void
  /**
   * Asks the pool to e-mail a reset code. Resolves identically for a known and an
   * unknown e-mail — the screen must not become a user-enumeration oracle (BR-08).
   */
  solicitarCodigoDeRedefinicao: (email: string) => Promise<void>
  /**
   * Completes the reset. Does **not** produce a session: `confirmResetPassword` leaves
   * the user signed out, so `/login` returns to the credentials form afterwards.
   */
  confirmarRedefinicaoDeSenha: (email: string, codigo: string, novaSenha: string) => Promise<void>
  /** The pool's password policy, for the "defina sua senha" step. */
  politicaDeSenha: () => Promise<PoliticaDeSenha>
  sair: (motivo?: MotivoSaida) => Promise<void>
  descartarAviso: () => void
  /** `'mock'` or `'cognito'` — only the login screen's dev hint reads this. */
  modo: ModoDeAuth
}

export type ModoDeAuth = 'mock' | 'cognito'

export const ContextoDeSessao = createContext<ContextoSessao | null>(null)

/**
 * The ONLY entry point to the session. AC-8 of story 0.3: swapping the mocked
 * session for the real Cognito pool must touch no screen — which holds exactly as
 * long as nothing imports an adapter directly.
 */
export function useSession(): ContextoSessao {
  const contexto = useContext(ContextoDeSessao)
  if (!contexto) {
    throw new Error('useSession() precisa estar dentro de <SessionProvider>.')
  }
  return contexto
}
